export type RawCA = Record<string, unknown>;

export type RowCfg =
  | { type: "section"; label: string }
  | {
      type: "data";
      label: string;
      tf: string;
      r1: string;
      isPct?: boolean;
      isMult?: boolean;
      isBool?: boolean;
      isText?: boolean;
      isSingle?: boolean;
    };

function parseNumeric(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const normalized = String(value).trim().replace(/,/g, "");
  if (normalized === "") return null;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function fmtNum(row: RawCA, key: string, isPct = false): string {
  const v = row[key];
  const n = parseNumeric(v);
  if (n === null) {
    const fallback = v === null || v === undefined ? "" : String(v).trim();
    return fallback || "—";
  }
  if (n === 0) return "0";
  if (isPct) return (n * 100).toFixed(2) + "%";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);
}

function fmtText(row: RawCA, key: string): string {
  const v = row[key];
  if (v === null || v === undefined || v === "") return "—";
  return String(v).trim();
}

function fmtMult(row: RawCA, key: string): string {
  const v = row[key];
  if (v === null || v === undefined || v === "") return "—";
  const n = parseFloat(String(v));
  if (isNaN(n)) return String(v);
  return n.toFixed(2) + "x";
}

function fmtBool(row: RawCA, key: string): string {
  const v = row[key];
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";

  const normalized = String(v).trim().toLowerCase();
  if (["true", "1", "yes", "y"].includes(normalized)) return "Yes";
  if (["false", "0", "no", "n"].includes(normalized)) return "No";

  return String(v);
}

export function resolveValue(
  raw: RawCA,
  cfg: Extract<RowCfg, { type: "data" }>,
  key: "tf" | "r1",
): string {
  const field = cfg[key];
  if (cfg.isText) return fmtText(raw, field);
  if (cfg.isMult) return fmtMult(raw, field);
  if (cfg.isBool) return fmtBool(raw, field);
  return fmtNum(raw, field, cfg.isPct);
}

export const ROW_CONFIG: RowCfg[] = [
  { type: "section", label: "General Information" },
  {
    type: "data",
    label: "Fund Name",
    tf: "gi_fund_name_val",
    r1: "gi_fund_name_val",
    isText: true,
    isSingle: true,
  },
  {
    type: "data",
    label: "LP Share (%)",
    tf: "gi_share_pct_val",
    r1: "gi_share_pct_val",
    isPct: true,
    isSingle: true,
  },
  {
    type: "data",
    label: "Reporting Date",
    tf: "gi_reporting_date_val",
    r1: "gi_reporting_date_val",
    isText: true,
    isSingle: true,
  },
  {
    type: "data",
    label: "Reporting Currency",
    tf: "gi_reporting_currency_val",
    r1: "gi_reporting_currency_val",
    isText: true,
    isSingle: true,
  },
  {
    type: "data",
    label: "Tax ID",
    tf: "gi_tax_id_val",
    r1: "gi_tax_id_val",
    isText: true,
    isSingle: true,
  },
  {
    type: "data",
    label: "Transparency Register",
    tf: "gi_transparency_register_val",
    r1: "gi_transparency_register_val",
    isBool: true,
    isSingle: true,
  },
  {
    type: "data",
    label: "Fund Status",
    tf: "gi_fund_status_val",
    r1: "gi_fund_status_val",
    isText: true,
    isSingle: true,
  },

  { type: "section", label: "Since Inception" },
  {
    type: "data",
    label: "Total Commitment",
    tf: "si_total_commitment_tf",
    r1: "si_total_commitment_r1",
  },
  {
    type: "data",
    label: "Unfunded Commitment",
    tf: "si_unfunded_commitment_tf",
    r1: "si_unfunded_commitment_r1",
  },
  {
    type: "data",
    label: "Paid in Capital (Total)",
    tf: "si_paid_in_capital_tf",
    r1: "si_paid_in_capital_r1",
  },
  {
    type: "data",
    label: "Distributions (Total)",
    tf: "si_distributions_tf",
    r1: "si_distributions_r1",
  },
  {
    type: "data",
    label: "  thereof Return of Capital",
    tf: "si_thereof_return_of_capital_tf",
    r1: "si_thereof_return_of_capital_r1",
  },
  {
    type: "data",
    label: "  thereof Gain/Loss",
    tf: "si_thereof_gain_loss_tf",
    r1: "si_thereof_gain_loss_r1",
  },
  {
    type: "data",
    label: "Recallable Distributions",
    tf: "si_recallable_distributions_tf",
    r1: "si_recallable_distributions_r1",
  },
  {
    type: "data",
    label: "  thereof Already Recalled",
    tf: "si_thereof_already_recalled_tf",
    r1: "si_thereof_already_recalled_r1",
  },
  {
    type: "data",
    label: "  thereof Current Recallable",
    tf: "si_thereof_current_recallable_tf",
    r1: "si_thereof_current_recallable_r1",
  },
  {
    type: "data",
    label: "Realized Gain/Losses",
    tf: "si_realized_gain_losses_tf",
    r1: "si_realized_gain_losses_r1",
  },
  {
    type: "data",
    label: "Unrealized Gain/Losses",
    tf: "si_unrealized_gain_losses_tf",
    r1: "si_unrealized_gain_losses_r1",
  },
  {
    type: "data",
    label: "Operating Income/Expenses",
    tf: "si_operating_income_expenses_tf",
    r1: "si_operating_income_expenses_r1",
  },
  {
    type: "data",
    label: "Equalisation Paid by LP",
    tf: "si_equalisation_paid_by_lp_tf",
    r1: "si_equalisation_paid_by_lp_r1",
  },
  {
    type: "data",
    label: "Equalisation Paid to LP",
    tf: "si_equalisation_paid_to_lp_tf",
    r1: "si_equalisation_paid_to_lp_r1",
  },
  {
    type: "data",
    label: "Management Fees Paid",
    tf: "si_management_fees_paid_tf",
    r1: "si_management_fees_paid_r1",
  },
  {
    type: "data",
    label: "NAV before Carry",
    tf: "si_nav_before_carry_tf",
    r1: "si_nav_before_carry_r1",
  },
  {
    type: "data",
    label: "Carried Interest Accrued",
    tf: "si_carried_interest_accrued_tf",
    r1: "si_carried_interest_accrued_r1",
  },
  {
    type: "data",
    label: "Carried Interest Paid",
    tf: "si_carried_interest_paid_tf",
    r1: "si_carried_interest_paid_r1",
  },
  {
    type: "data",
    label: "NAV after Carry",
    tf: "si_nav_after_carry_tf",
    r1: "si_nav_after_carry_r1",
  },
  {
    type: "data",
    label: "TVPI",
    tf: "si_tvpi_tf",
    r1: "si_tvpi_r1",
    isMult: true,
  },
  {
    type: "data",
    label: "DPI",
    tf: "si_dpi_tf",
    r1: "si_dpi_r1",
    isMult: true,
  },
  {
    type: "data",
    label: "RVPI",
    tf: "si_rvpi_tf",
    r1: "si_rvpi_r1",
    isMult: true,
  },
  {
    type: "data",
    label: "Gross IRR",
    tf: "si_gross_irr_tf",
    r1: "si_gross_irr_r1",
    isPct: true,
  },
  {
    type: "data",
    label: "Net IRR",
    tf: "si_net_irr_tf",
    r1: "si_net_irr_r1",
    isPct: true,
  },

  { type: "section", label: "Current Quarter" },
  {
    type: "data",
    label: "Paid in Capital",
    tf: "cq_paid_in_capital_tf",
    r1: "cq_paid_in_capital_r1",
  },
  {
    type: "data",
    label: "Distributions",
    tf: "cq_distributions_tf",
    r1: "cq_distributions_r1",
  },
  {
    type: "data",
    label: "  thereof Return of Capital",
    tf: "cq_thereof_return_of_capital_tf",
    r1: "cq_thereof_return_of_capital_r1",
  },
  {
    type: "data",
    label: "  thereof Gain/Loss",
    tf: "cq_thereof_gain_loss_tf",
    r1: "cq_thereof_gain_loss_r1",
  },
  {
    type: "data",
    label: "Recallable Distributions",
    tf: "cq_recallable_distributions_tf",
    r1: "cq_recallable_distributions_r1",
  },
  {
    type: "data",
    label: "Realized Gain/Losses",
    tf: "cq_realized_gain_losses_tf",
    r1: "cq_realized_gain_losses_r1",
  },
  {
    type: "data",
    label: "Operating Income/Expenses",
    tf: "cq_operating_income_expenses_tf",
    r1: "cq_operating_income_expenses_r1",
  },
  {
    type: "data",
    label: "Equalisation Paid by LP",
    tf: "cq_equalisation_paid_by_r1_tf",
    r1: "cq_equalisation_paid_by_r1_r1",
  },
  {
    type: "data",
    label: "Equalisation Paid to LP",
    tf: "cq_equalisation_paid_to_r1_tf",
    r1: "cq_equalisation_paid_to_r1_r1",
  },
  {
    type: "data",
    label: "Management Fees Paid",
    tf: "cq_management_fees_paid_tf",
    r1: "cq_management_fees_paid_r1",
  },
  {
    type: "data",
    label: "NAV before Carry",
    tf: "cq_nav_before_carry_tf",
    r1: "cq_nav_before_carry_r1",
  },
  {
    type: "data",
    label: "Carried Interest Accrued",
    tf: "cq_carried_interest_accrued_tf",
    r1: "cq_carried_interest_accrued_r1",
  },
  {
    type: "data",
    label: "Carried Interest Paid",
    tf: "cq_carried_interest_paid_tf",
    r1: "cq_carried_interest_paid_r1",
  },
  {
    type: "data",
    label: "NAV after Carry",
    tf: "cq_nav_after_carry_tf",
    r1: "cq_nav_after_carry_r1",
  },
];
